import { useCallback, useEffect, useRef, useState } from 'react';
import { useI18n } from '../i18n';
import {
  clampCropTransform,
  createDefaultCropTransform,
  cropTransformToPreviewStyle,
  loadImageElementFromFile,
  revokeProfileAvatarObjectUrl,
  PROFILE_AVATAR_CROP_MAX_ZOOM,
  PROFILE_AVATAR_CROP_MIN_ZOOM,
  PROFILE_AVATAR_CROP_PREVIEW_SIZE,
  renderCroppedAvatarDataUrl,
  type ProfileAvatarCropTransform,
} from '../utils/profileAvatarCrop';
import styles from './ProfileAvatarCropPanel.module.css';

type ProfileAvatarCropPanelProps = {
  file: File;
  onCancel: () => void;
  onSave: (dataUrl: string) => void;
};

export function ProfileAvatarCropPanel({ file, onCancel, onSave }: ProfileAvatarCropPanelProps) {
  const { t, language } = useI18n();
  const dir = language === 'he' ? 'rtl' : 'ltr';
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [transform, setTransform] = useState<ProfileAvatarCropTransform>(createDefaultCropTransform);
  const [dragging, setDragging] = useState(false);
  const dragStartRef = useRef<{ x: number; y: number; offsetX: number; offsetY: number } | null>(null);
  const previewImageRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    void loadImageElementFromFile(file)
      .then((loaded) => {
        if (cancelled) {
          revokeProfileAvatarObjectUrl(loaded);
          return;
        }
        previewImageRef.current = loaded;
        setImage(loaded);
        setTransform(createDefaultCropTransform());
      })
      .catch(() => {
        if (!cancelled) {
          onCancel();
        }
      });
    return () => {
      cancelled = true;
      revokeProfileAvatarObjectUrl(previewImageRef.current);
      previewImageRef.current = null;
    };
  }, [file, onCancel]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCancel();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onCancel]);

  const updateTransform = useCallback(
    (next: ProfileAvatarCropTransform) => {
      if (!image) {
        return;
      }
      setTransform(clampCropTransform(next, image.width, image.height));
    },
    [image],
  );

  const onPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!image) {
        return;
      }
      event.currentTarget.setPointerCapture(event.pointerId);
      dragStartRef.current = {
        x: event.clientX,
        y: event.clientY,
        offsetX: transform.offsetX,
        offsetY: transform.offsetY,
      };
      setDragging(true);
    },
    [image, transform.offsetX, transform.offsetY],
  );

  const onPointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const start = dragStartRef.current;
      if (!start || !image) {
        return;
      }
      updateTransform({
        zoom: transform.zoom,
        offsetX: start.offsetX + (event.clientX - start.x),
        offsetY: start.offsetY + (event.clientY - start.y),
      });
    },
    [image, transform.zoom, updateTransform],
  );

  const onPointerUp = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    dragStartRef.current = null;
    setDragging(false);
  }, []);

  const onZoomChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      updateTransform({ ...transform, zoom: Number(event.target.value) });
    },
    [transform, updateTransform],
  );

  const onConfirm = useCallback(() => {
    if (!image) {
      return;
    }
    const dataUrl = renderCroppedAvatarDataUrl(image, transform);
    if (dataUrl) {
      onSave(dataUrl);
    }
  }, [image, onSave, transform]);

  const onBackdropPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (event.target === event.currentTarget) {
        onCancel();
      }
    },
    [onCancel],
  );

  const placement =
    image &&
    cropTransformToPreviewStyle(transform, image.width, image.height, PROFILE_AVATAR_CROP_PREVIEW_SIZE);

  return (
    <div
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-label={t('profile.hub.cropTitle')}
      dir={dir}
      onPointerDown={onBackdropPointerDown}
    >
      <div className={styles.sheet}>
        <div className={styles.headerRow}>
          <h2 className={styles.title}>{t('profile.hub.cropTitle')}</h2>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onCancel}
            aria-label={t('common.close')}
          >
            ✕
          </button>
        </div>

        <div className={styles.previewWrap}>
          <div
            className={[styles.previewFrame, dragging ? styles.previewFrameDragging : ''].join(' ')}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          >
            {image && placement ? (
              <img
                src={image.src}
                alt=""
                className={styles.previewImage}
                draggable={false}
                style={{
                  width: placement.width,
                  height: placement.height,
                  left: placement.x,
                  top: placement.y,
                }}
              />
            ) : null}
          </div>
        </div>

        <label className={styles.zoomField}>
          <span className={styles.zoomLabel}>{t('profile.hub.cropZoom')}</span>
          <input
            type="range"
            className={styles.zoomInput}
            min={PROFILE_AVATAR_CROP_MIN_ZOOM}
            max={PROFILE_AVATAR_CROP_MAX_ZOOM}
            step={0.01}
            value={transform.zoom}
            onChange={onZoomChange}
            disabled={!image}
          />
        </label>

        <div className={styles.actions}>
          <button type="button" className={styles.secondaryBtn} onClick={onCancel}>
            {t('profile.hub.cropCancel')}
          </button>
          <button type="button" className={styles.primaryBtn} onClick={onConfirm} disabled={!image}>
            {t('profile.hub.cropSave')}
          </button>
        </div>
      </div>
    </div>
  );
}
